<script setup lang="ts">
import { computed } from 'vue';

import type { IFormSelectInputOption } from '@modules/forms/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VFormSelectInput from '@modules/forms/components/inputs/VFormSelectInput/VFormSelectInput.vue';
import { useI18n } from '@modules/i18n/composables';
import { useUserSupervision } from '@modules/user/composables';
import { isNullOrUndefined } from '@utils/guards';

import type { IReportTabsEmits } from '../VReportTabs/types';
import type { IReportTabsMobileProps } from './types';

const props = defineProps<IReportTabsMobileProps>();

const emit = defineEmits<IReportTabsEmits>();

const { getTranslation } = useI18n();
const { isSupervision } = useUserSupervision();

const tabsForSelect = computed<IFormSelectInputOption<number>[]>(() => props.tabs.map(({ name, shortcode, id }) => ({
  label: name || getTranslation(props.translationKey, shortcode, { id }).value,
  value: id,
})));

function onSelect(id?: number): void {
  if (isNullOrUndefined(id))
    return;

  emit('select', id);
}

function onToggle(id?: number): void {
  if (isNullOrUndefined(id))
    return;

  emit('expand-toggle', id);
}

function onRemove(id?: number, name?: Maybe<string>): void {
  if (isNullOrUndefined(id))
    return;

  emit('remove', id, name);
}
</script>

<template>
  <div v-data-qa-auto-id="'VReportTabsMobile'" class="flex items-center gap-2">
    <div class="w-full input-max-width">
      <VFormSelectInput
        :options="tabsForSelect"
        :value="activeTab?.id"
        :label="selectLabel"
        is-full-width
        hide-errors
        @change="onSelect"
      />
    </div>
    <div
      v-if="activeTab?.id !== 0"
      class="flex-none"
    >
      <VIconButton
        :icon="activeTab?.expanded ? 'pi-angle-up' : 'pi-angle-down'"
        @click="onToggle(activeTab?.id)"
      />
    </div>
    <div
      v-if="activeTab?.id !== 0 && !activeTab?.read_only"
      class="flex-none"
    >
      <VIconButton
        :loading="isRemoveLoading"
        :disabled="isSupervision"
        icon="pi-times"
        @click="onRemove(activeTab?.id, activeTab?.name)"
      />
    </div>
  </div>
  <div class="mt-4">
    <VButton
      :loading="isAddLoading"
      :label="addLabel"
      :disabled="isSupervision"
      icon="pi pi-plus-circle"
      text
      @click="emit('add')"
    />
  </div>
</template>
